/**
 * Дескриптор открытой страницы, собранный из живого приложения.
 *
 * Разделение с `describePage` намеренное: там чистая функция и вся форма
 * дескриптора, здесь — только откуда берутся слагаемые. Отбор читается прямо из
 * сторов доски и списка, своей копии фильтров у ассистента нет: она разошлась
 * бы с фильтр-баром на первом же переключении вида, и человек получил бы ответ
 * про выборку, которой на экране уже нет.
 */
import { useTextSelection } from '@vueuse/core'
import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'

import { useBoardStore } from '@/features/board/store'
import { useIssuesStore } from '@/features/issues/store'
import { useProjectsStore } from '@/features/projects/store'

import { describePage, type PageDescriptor } from './context'

/**
 * Атрибут корня панели.
 *
 * По нему отбрасывается выделение внутри самого ассистента: скопировать кусок
 * его ответа — обычное дело, и без проверки этот кусок уехал бы контекстом
 * следующего же вопроса, а модель приняла бы собственные слова за увиденное
 * человеком на странице.
 */
export const PANEL_MARKER = 'data-assistant-panel'

/** Выделение началось на странице, а не внутри панели ассистента. */
export const isOutsidePanel = (node: Node | null | undefined): boolean => {
  const element = node instanceof Element ? node : (node?.parentElement ?? null)

  return !element?.closest(`[${PANEL_MARKER}]`)
}

export const useAssistantContext = (): ComputedRef<PageDescriptor> => {
  const route = useRoute()
  const projects = useProjectsStore()
  const board = useBoardStore()
  const issues = useIssuesStore()

  const { text, selection } = useTextSelection()

  const outsideSelection = computed(() =>
    isOutsidePanel(selection.value?.anchorNode) ? text.value : '',
  )

  // Отбор ровно того экрана, который открыт: у доски и списка фильтр-бар общий,
  // но выборки разные, и отдавать чужую — врать о том, что человек видит.
  const filters = computed(() => {
    if (route.name === 'board') return board.filters
    if (route.name === 'issues') return issues.filters

    return undefined
  })

  // В адресе ключ проекта, а человек говорит о нём по имени: «на доске
  // Разработки», а не «на доске DEV».
  const projectName = computed(
    () => projects.items.find((item) => item.key === String(route.params.key ?? ''))?.name ?? '',
  )

  return computed(() =>
    describePage(route, {
      projectName: projectName.value,
      filters: filters.value,
      selection: outsideSelection.value,
    }),
  )
}
